import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { ClockCounterClockwise, ArrowClockwise, UserMinus, UserPlus, UserGear, ShieldCheck, User as UserIcon, Trash, Plus, PencilSimple, Key } from "@phosphor-icons/react";

const ACTION_META = {
  "user.create": { label: "Création", color: "#10B981", icon: Plus },
  "user.update": { label: "Modification", color: "#3B82F6", icon: PencilSimple },
  "user.delete": { label: "Suppression", color: "#EF4444", icon: Trash },
  "user.deactivate": { label: "Désactivation", color: "#F59E0B", icon: UserMinus },
  "user.reactivate": { label: "Réactivation", color: "#10B981", icon: UserPlus },
  "user.role_change": { label: "Changement de rôle", color: "#8B5CF6", icon: UserGear },
  "user.permissions_override": { label: "Override permissions", color: "#EC4899", icon: ShieldCheck },
  "user.password_reset": { label: "Reset mot de passe", color: "#F59E0B", icon: Key },
};

function fmt(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString("fr-FR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function DiffLines({ before, after }) {
  const keys = Array.from(new Set([...Object.keys(before || {}), ...Object.keys(after || {})]));
  if (!keys.length) return null;
  return (
    <div className="mt-2 space-y-1 text-[11px] font-mono">
      {keys.map((k) => {
        const b = JSON.stringify(before?.[k] ?? null);
        const a = JSON.stringify(after?.[k] ?? null);
        return (
          <div key={k} className="flex gap-2">
            <span className="text-white/40 shrink-0">{k}:</span>
            <span className="text-red-300 line-through">{b}</span>
            <span className="text-white/40">→</span>
            <span className="text-emerald-300">{a}</span>
          </div>
        );
      })}
    </div>
  );
}

export default function AuditLogPanel() {
  const [entries, setEntries] = useState([]);
  const [actions, setActions] = useState([]);
  const [actionFilter, setActionFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = actionFilter && actionFilter !== "all" ? { action: actionFilter } : {};
      const r = await api.get("/admin/audit-log", { params });
      setEntries(r.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    api.get("/admin/audit-log/actions").then((r) => setActions(r.data.actions || [])).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [actionFilter]);

  return (
    <div className="space-y-3" data-testid="audit-log-panel">
      <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-4">
        <div className="flex items-center justify-between gap-3 mb-3">
          <div className="flex items-center gap-2">
            <ClockCounterClockwise size={20} className="text-[#005CFF]" weight="duotone" />
            <div>
              <div className="font-display font-semibold">Journal d&apos;audit</div>
              <div className="text-[11px] text-white/50">Traçabilité des actions admin sur les utilisateurs</div>
            </div>
          </div>
          <Button onClick={load} variant="ghost" className="border border-white/10 h-9" data-testid="audit-refresh-btn">
            <ArrowClockwise size={14} weight="bold" />
          </Button>
        </div>

        <div className="flex items-center gap-2 mb-3">
          <span className="text-[11px] uppercase tracking-widest text-white/50 shrink-0">Filtre action</span>
          <Select value={actionFilter} onValueChange={setActionFilter}>
            <SelectTrigger className="bg-white/5 border-white/10 h-9 text-xs" data-testid="audit-action-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#111] border-white/10 text-white">
              <SelectItem value="all">Toutes les actions</SelectItem>
              {actions.map((a) => (
                <SelectItem key={a} value={a}>{ACTION_META[a]?.label || a}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="text-center text-xs text-white/40 py-8">Chargement…</div>
        ) : entries.length === 0 ? (
          <div className="rounded-xl bg-white/[0.02] border border-dashed border-white/10 p-8 text-center text-white/40 text-sm">
            Aucune entrée pour ce filtre
          </div>
        ) : (
          <div className="space-y-2">
            {entries.map((e) => {
              const meta = ACTION_META[e.action] || { label: e.action, color: "#9CA3AF", icon: UserIcon };
              const Icon = meta.icon;
              return (
                <div key={e.id} className="rounded-xl bg-white/[0.03] border border-white/10 p-3" data-testid={`audit-entry-${e.id}`}>
                  <div className="flex items-start gap-3">
                    <div
                      className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                      style={{ background: `${meta.color}22`, color: meta.color }}
                    >
                      <Icon size={16} weight="duotone" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span
                          className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded-full"
                          style={{ background: `${meta.color}22`, color: meta.color }}
                        >
                          {meta.label}
                        </span>
                        <span className="text-xs text-white/70">
                          <b>{e.actor_name || "?"}</b>
                          {e.meta?.target_name && (<> → <b>{e.meta.target_name}</b></>)}
                        </span>
                      </div>
                      <div className="text-[10px] text-white/40 mt-0.5">{fmt(e.created_at)}</div>
                      {(Object.keys(e.before || {}).length > 0 || Object.keys(e.after || {}).length > 0) && (
                        <DiffLines before={e.before} after={e.after} />
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
